import React, { useState, useEffect, useRef } from 'react';

const carData = {
  "Peugeot": ["208", "308", "2008", "3008", "5008", "508", "Rifter", "Partner", "108"],
  "Renault": ["Clio", "Megane", "Captur", "Kadjar", "Austral", "Arkana", "Kangoo", "Twingo", "Zoe", "Espace", "Trafic"],
  "Citroën": ["C3", "C4", "C5 Aircross", "C3 Aircross", "Berlingo", "C4 Cactus", "C1"],
  "Dacia": ["Sandero", "Duster", "Jogger", "Spring", "Logan"],
  "Volkswagen": ["Golf", "Polo", "Tiguan", "T-Roc", "Passat", "T-Cross", "Touran", "Up!"],
  "Audi": ["A1", "A3", "A4", "Q3", "Q5", "A6", "Q2"],
  "BMW": ["Série 1", "Série 3", "X1", "X3", "Série 5", "X5"],
  "Mercedes-Benz": ["Classe A", "Classe C", "GLC", "GLA", "Classe E", "Classe B"],
  "Toyota": ["Yaris", "Corolla", "C-HR", "RAV4", "Aygo", "Yaris Cross"],
  "Ford": ["Fiesta", "Focus", "Puma", "Kuga", "Transit"],
  "Fiat": ["500", "Panda", "Tipo", "Ducato"],
  "Nissan": ["Qashqai", "Juke", "Micra", "X-Trail"],
  "Opel": ["Corsa", "Mokka", "Astra", "Crossland"],
  "Seat": ["Ibiza", "Leon", "Arona", "Ateca"],
  "Skoda": ["Fabia", "Octavia", "Kamiq", "Karoq", "Kodiaq"],
  "Hyundai": ["Tucson", "Kona", "i20", "i30", "Santa Fe"],
  "Kia": ["Sportage", "Picanto", "Ceed", "Niro", "Stonic"],
};

const CarListingForm = ({ listing, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    mileage: '',
    fuel_type: 'Essence',
    transmission: 'Manuelle',
    status: 'available',
    location: '',
    youtube_link: '',
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  
  const [errors, setErrors] = useState({});

  const [showMakeSuggestions, setShowMakeSuggestions] = useState(false);
  const [showModelSuggestions, setShowModelSuggestions] = useState(false);
  
  const makeRef = useRef(null);
  const modelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (makeRef.current && !makeRef.current.contains(event.target)) {
        setShowMakeSuggestions(false);
      }
      if (modelRef.current && !modelRef.current.contains(event.target)) {
        setShowModelSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (listing) {
      setFormData({
        title: listing.title || '',
        description: listing.description || '',
        price: listing.price || '',
        make: listing.make || '',
        model: listing.model || '',
        year: listing.year || new Date().getFullYear(),
        mileage: listing.mileage || '',
        fuel_type: listing.fuel_type || 'Essence',
        transmission: listing.transmission || 'Manuelle',
        status: listing.status || 'available',
        location: listing.location || '',
        youtube_link: listing.youtube_link || '',
      });
      
      // Charger les aperçus des images existantes
      if (listing.images && listing.images.length > 0) {
        const previews = listing.images.map(img => ({
          url: `https://christophedepanne.infocolab.online/api/files/car_listings/${listing.id}/${img}`,
          name: img,
          existing: true
        }));
        setImagePreview(previews);
      }
      
      if (listing.cover_image) {
        setCoverImagePreview({
          url: `https://christophedepanne.infocolab.online/api/files/car_listings/${listing.id}/${listing.cover_image}`,
          name: listing.cover_image,
          existing: true
        });
      }
    }
  }, [listing]);

  // Fonction pour compresser une image
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/webp', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const newImageFiles = [];
    const newPreviews = [];
    
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        const compressedFile = await compressImage(file);
        // On remplace l'extension d'origine par .webp
        const newFileName = file.name.replace(/\.[^/.]+$/, "") + ".webp";
        const compressedFileWithName = new File([compressedFile], newFileName, {
          type: 'image/webp'
        });
        
        newImageFiles.push(compressedFileWithName);
        
        const preview = {
          url: URL.createObjectURL(compressedFileWithName),
          name: file.name,
          existing: false
        };
        newPreviews.push(preview);
      }
    }
    
    setImageFiles(prev => [...prev, ...newImageFiles]);
    setImagePreview(prev => [...prev, ...newPreviews]);
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const compressedFile = await compressImage(file);
      const newFileName = file.name.replace(/\.[^/.]+$/, "") + ".webp";
      const compressedFileWithName = new File([compressedFile], newFileName, {
        type: 'image/webp'
      });
      
      setCoverImageFile(compressedFileWithName);
      setCoverImagePreview({
        url: URL.createObjectURL(compressedFileWithName),
        name: file.name,
        existing: false
      });
    }
  };

  const removeCoverImage = () => {
    if (coverImagePreview && !coverImagePreview.existing) {
      URL.revokeObjectURL(coverImagePreview.url);
    }
    setCoverImageFile(null);
    setCoverImagePreview(null);
  };

  const removeImage = (index) => {
    setImagePreview(prev => {
      const newPreviews = [...prev];
      const removed = newPreviews.splice(index, 1)[0];
      
      if (!removed.existing) {
        URL.revokeObjectURL(removed.url);
        setImageFiles(prevFiles => {
          const newFiles = [...prevFiles];
          const fileIndex = prevFiles.findIndex(f => f.name === removed.name);
          if (fileIndex > -1) {
            newFiles.splice(fileIndex, 1);
          }
          return newFiles;
        });
      }
      
      return newPreviews;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Le titre est requis';
    if (!formData.price) newErrors.price = 'Le prix est requis';
    if (!formData.make.trim()) newErrors.make = 'La marque est requise';
    if (!formData.model.trim()) newErrors.model = 'Le modèle est requis';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const submitData = {
        ...formData,
        imageFiles: imageFiles,
        existingImages: imagePreview.filter(img => img.existing).map(img => img.name),
        coverImageFile: coverImageFile
      };
      onSubmit(submitData);
    }
  };

  return (
    <div className="article-form-overlay">
      <div className="article-form-container">
        <div className="article-form-header">
          <h2>{listing ? 'Modifier l\'annonce' : 'Nouvelle annonce véhicule'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="article-form">
          <div className="form-row">
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="title">Titre de l'annonce *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Ex: Peugeot 208 GT Line très bon état"
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="error-text">{errors.title}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" ref={makeRef}>
              <label htmlFor="make">Marque *</label>
              <input
                type="text"
                id="make"
                name="make"
                value={formData.make}
                onChange={handleChange}
                onFocus={() => setShowMakeSuggestions(true)}
                placeholder="Ex: Peugeot"
                className={errors.make ? 'error' : ''}
                autoComplete="off"
              />
              {showMakeSuggestions && (
                <div className="suggestions-list">
                  {Object.keys(carData)
                    .filter(make => make.toLowerCase().includes(formData.make.toLowerCase()))
                    .map(make => (
                      <div 
                        key={make} 
                        className="suggestion-item"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, make: make, model: '' }));
                          setShowMakeSuggestions(false);
                          if (errors.make) setErrors(prev => ({...prev, make: ''}));
                        }}
                      >
                        {make}
                      </div>
                    ))}
                </div>
              )}
              {errors.make && <span className="error-text">{errors.make}</span>}
            </div>

            <div className="form-group" ref={modelRef}>
              <label htmlFor="model">Modèle *</label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                onFocus={() => setShowModelSuggestions(true)}
                placeholder="Ex: 208"
                className={errors.model ? 'error' : ''}
                autoComplete="off"
              />
              {showModelSuggestions && formData.make && carData[formData.make] && (
                <div className="suggestions-list">
                  {carData[formData.make]
                    .filter(model => model.toLowerCase().includes(formData.model.toLowerCase()))
                    .map(model => (
                      <div 
                        key={model} 
                        className="suggestion-item"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, model: model }));
                          setShowModelSuggestions(false);
                          if (errors.model) setErrors(prev => ({...prev, model: ''}));
                        }}
                      >
                        {model}
                      </div>
                    ))}
                </div>
              )}
              {errors.model && <span className="error-text">{errors.model}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Prix (€) *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="Ex: 15000"
                className={errors.price ? 'error' : ''}
              />
              {errors.price && <span className="error-text">{errors.price}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="year">Année</label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                placeholder="Ex: 2020"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mileage">Kilométrage</label>
              <input
                type="number"
                id="mileage"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                placeholder="Ex: 45000"
              />
            </div>
            <div className="form-group">
              <label htmlFor="fuel_type">Carburant</label>
              <select id="fuel_type" name="fuel_type" value={formData.fuel_type} onChange={handleChange}>
                <option value="Essence">Essence</option>
                <option value="Diesel">Diesel</option>
                <option value="Électrique">Électrique</option>
                <option value="Hybride">Hybride</option>
                <option value="GPL">GPL</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="transmission">Boîte de vitesse</label>
              <select id="transmission" name="transmission" value={formData.transmission} onChange={handleChange}>
                <option value="Manuelle">Manuelle</option>
                <option value="Automatique">Automatique</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="location">Localisation / Ville</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Paris"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="youtube_link">Lien vidéo YouTube</label>
            <input
              type="text"
              id="youtube_link"
              name="youtube_link"
              value={formData.youtube_link}
              onChange={handleChange}
              placeholder="Ex: https://www.youtube.com/watch?v=..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="cover_image">Photo de couverture (Miniature)</label>
            <input
              type="file"
              id="cover_image"
              name="cover_image"
              onChange={handleCoverImageChange}
              accept="image/*"
              className="file-input"
            />
            <div className="file-input-help">
              Une seule image. Elle sera affichée en miniature et en premier sur l'annonce.
            </div>
            {coverImagePreview && (
              <div className="image-preview-grid">
                <div className="image-preview-item" style={{ border: '2px solid #3b82f6' }}>
                  <img src={coverImagePreview.url} alt="Aperçu couverture" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={removeCoverImage}
                    title="Supprimer"
                  >
                    ×
                  </button>
                  <div className="image-name">Couverture</div>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="images">Galerie photos (Autres images)</label>
            <input
              type="file"
              id="images"
              name="images"
              onChange={handleImageChange}
              accept="image/*"
              multiple
              className="file-input"
            />
            <div className="file-input-help">
              Formats acceptés: JPG, PNG. Les images seront compressées automatiquement.
            </div>
            
            {imagePreview.length > 0 && (
              <div className="image-preview-grid">
                {imagePreview.map((preview, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={preview.url} alt={`Aperçu ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => removeImage(index)}
                      title="Supprimer"
                    >
                      ×
                    </button>
                    <div className="image-name">{preview.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description détaillée</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Décrivez l'état général, l'historique d'entretien, les options..."
              rows="6"
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Statut de l'annonce</label>
            <select id="status" name="status" value={formData.status} onChange={handleChange}>
              <option value="available">Disponible</option>
              <option value="reserved">Réservé</option>
              <option value="sold">Vendu</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">Annuler</button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Enregistrement...' : (listing ? 'Mettre à jour' : 'Publier l\'annonce')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .article-form-overlay {
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0, 0, 0, 0.7); display: flex; align-items: center; justify-content: center;
            z-index: 2000; padding: 20px; box-sizing: border-box;
          }
          .article-form-container {
            background: white; border-radius: 12px; width: 100%; max-width: 800px;
            max-height: 90vh; overflow-y: auto; box-shadow: 0 25px 50px rgba(0,0,0,0.25);
            animation: modalAppear 0.3s ease-out; box-sizing: border-box;
          }
          @keyframes modalAppear {
            from { opacity: 0; transform: translateY(-50px) scale(0.9); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .article-form-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: 24px 24px 0; border-bottom: 1px solid #e5e7eb; margin-bottom: 24px;
          }
          .article-form-header h2 { margin: 0; font-size: 1.5rem; font-weight: 600; color: #1f2937; }
          .close-btn { background: none; border: none; cursor: pointer; color: #6b7280; padding: 4px; border-radius: 4px; transition: all 0.2s; }
          .close-btn:hover { background: #f3f4f6; color: #374151; }
          .article-form { padding: 0 24px 24px; }
          .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .form-group { margin-bottom: 20px; position: relative; }
          .form-group label { display: block; margin-bottom: 6px; font-weight: 500; color: #374151; font-size: 0.9rem; }
          .form-group input, .form-group select, .form-group textarea {
            width: 100%; padding: 12px 16px; border: 2px solid #e5e7eb; border-radius: 8px; font-size: 1rem; box-sizing: border-box; font-family: inherit;
          }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
            outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
          }
          .form-group input.error { border-color: #dc2626; }
          .form-group textarea { resize: vertical; min-height: 100px; }
          .error-text { color: #dc2626; font-size: 0.8rem; margin-top: 4px; display: block; }
          
          .suggestions-list {
            position: absolute; top: 100%; left: 0; right: 0; background: white; border: 1px solid #e5e7eb;
            border-radius: 8px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); max-height: 200px;
            overflow-y: auto; z-index: 10; margin-top: 4px;
          }
          .suggestion-item { padding: 10px 16px; cursor: pointer; transition: background-color 0.2s; }
          .suggestion-item:hover { background-color: #f3f4f6; }
          
          .file-input { padding: 12px 16px; border: 2px dashed #d1d5db; border-radius: 8px; background: #f9fafb; cursor: pointer; }
          .file-input-help { font-size: 0.8rem; color: #6b7280; margin-top: 4px; }
          .image-preview-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 12px; margin-top: 16px; }
          .image-preview-item { position: relative; border-radius: 8px; overflow: hidden; background: #f3f4f6; aspect-ratio: 1; }
          .image-preview-item img { width: 100%; height: 100%; object-fit: cover; }
          .remove-image-btn {
            position: absolute; top: 4px; right: 4px; background: rgba(239,68,68,0.9); color: white; border: none; border-radius: 50%;
            width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
          }
          .remove-image-btn:hover { background: rgba(239,68,68,1); transform: scale(1.1); }
          .image-name { position: absolute; bottom: 0; left: 0; right: 0; background: rgba(0,0,0,0.7); color: white; padding: 4px 6px; font-size: 0.7rem; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; }
          .form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 32px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
          .btn-cancel { padding: 12px 24px; border: 1px solid #d1d5db; background: white; color: #374151; border-radius: 8px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
          .btn-cancel:hover { background: #f9fafb; border-color: #9ca3af; }
          .btn-submit { padding: 12px 24px; border: none; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border-radius: 8px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
          .btn-submit:hover:not(:disabled) { background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%); transform: translateY(-1px); }
          .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
          @media (max-width: 768px) {
            .article-form-overlay { padding: 15px; }
            .article-form-container { margin: 0; width: 100%; max-width: 100%; }
            .form-row { grid-template-columns: 1fr; gap: 0; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default CarListingForm;
